import {
  defaultLoadableState,
  type LoadableState,
  useLoadableState
} from '@carvana/merch-react-hooks';
import React, { createContext, useContext, useMemo, useState } from 'react';
import SoftPullStatus from '~/constants/softPullStatus';
import type { UserFinancing } from '~/contracts/vdp';

export type ContextType = {
  estimatedAmountFinanced: number;
  isError: boolean;
  isLoaded: boolean;
  isLoading: boolean;
  setEstimatedAmountFinanced: (amount: number) => void;
  setError: (error: boolean) => void;
  setIsLoaded: (isLoaded: boolean) => void;
  setLoading: (loading: boolean) => void;
  setTriggerUserFinancingRequest: (trigger: number) => void;
  triggerUserFinancingRequest: number;
  userFinancing: LoadableState<UserFinancing | null>;
};

const defaultContext: ContextType = {
  estimatedAmountFinanced: 0,
  isError: false,
  isLoaded: false,
  isLoading: false,
  setEstimatedAmountFinanced() {},
  setError() {},
  setLoading() {},
  setIsLoaded() {},
  setTriggerUserFinancingRequest() {},
  triggerUserFinancingRequest: 0,
  userFinancing: defaultLoadableState<UserFinancing | null>(null)
};

const FinanceContext = createContext<ContextType>(defaultContext);

export function FinanceContextProvider({ children }: React.PropsWithChildren) {
  const [estimatedAmountFinanced, setEstimatedAmountFinanced] = useState(
    defaultContext.estimatedAmountFinanced
  );
  const [isError, setError] = useState(defaultContext.isError);
  const [isLoading, setLoading] = useState(defaultContext.isLoading);
  const [isLoaded, setIsLoaded] = useState(defaultContext.isLoading);
  const [triggerUserFinancingRequest, setTriggerUserFinancingRequest] =
    useState(0);
  const userFinancing = useLoadableState<UserFinancing | null>(
    defaultContext.userFinancing.data
  );

  const value = useMemo<ContextType>(
    () => ({
      estimatedAmountFinanced,
      isError,
      isLoaded,
      isLoading,
      setEstimatedAmountFinanced,
      setError,
      setIsLoaded,
      setLoading,
      setTriggerUserFinancingRequest,
      triggerUserFinancingRequest,
      userFinancing
    }),
    [
      estimatedAmountFinanced,
      isError,
      isLoaded,
      isLoading,
      triggerUserFinancingRequest,
      userFinancing
    ]
  );

  return (
    <FinanceContext.Provider value={value}>{children}</FinanceContext.Provider>
  );
}

FinanceContextProvider.displayName = 'FinanceContextProvider';

export default FinanceContext;

export function useSoftPullStatus(): SoftPullStatus {
  const userFinancing = useContext(FinanceContext).userFinancing.data;

  if (userFinancing?.isValid == null) {
    return SoftPullStatus.None;
  }

  return userFinancing.isValid ? SoftPullStatus.Active : SoftPullStatus.Expired;
}
